import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1682264788192-9abdec90c425?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxuZWFwb2xpdGFuJTIwcGl6emElMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODY3MjUzMDJ8MA&ixlib=rb-4.1.0&q=85";
const CATS = ["all", "fritti", "focacce", "signature", "classiche", "rosse", "cocktail", "dolci"];
const DEFAULT_CAT_IMG = {
  all: "https://images.unsplash.com/photo-1590947132387-155cc02f3212?q=80&w=1200&auto=format&fit=crop",
  fritti: "https://images.unsplash.com/photo-1562967914-608f82629710?q=80&w=1200&auto=format&fit=crop",
  focacce: "https://images.unsplash.com/photo-1604382354936-07c5d9983bd3?q=80&w=1200&auto=format&fit=crop",
  signature: FALLBACK_IMG,
  classiche: "https://images.unsplash.com/photo-1574071318508-1cdbab80d002?q=80&w=1200&auto=format&fit=crop",
  rosse: "https://images.unsplash.com/photo-1663610561381-e70493d31c0f?q=80&w=1200&auto=format&fit=crop",
  cocktail: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?q=80&w=1200&auto=format&fit=crop",
  dolci: "https://images.unsplash.com/photo-1714385905983-6f8e06fffae1?q=80&w=1200&auto=format&fit=crop",
};

export function MenuSection() {
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [viewCat, setViewCat] = useState("all");
  const [siteImages, setSiteImages] = useState({});
  const [loading, setLoading] = useState(true);
  const groupRefs = useRef({});

  useEffect(() => {
    api.get("/menu").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/site-images").then((r) => setSiteImages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (cat !== "all") return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setViewCat(e.target.dataset.cat); });
    }, { rootMargin: "-160px 0px -65% 0px" });
    Object.values(groupRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [cat, items]);

  const filtered = cat === "all" ? items : items.filter((i) => i.category === cat);
  const effectiveCat = cat === "all" ? viewCat : cat;
  const previewImg = siteImages[`cat_${effectiveCat}`] || DEFAULT_CAT_IMG[effectiveCat] || FALLBACK_IMG;
  const imgUrl = (u) => (u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
  const groups = cat === "all"
    ? CATS.filter((c) => c !== "all").map((c) => ({ cat: c, items: items.filter((i) => i.category === c) })).filter((g) => g.items.length > 0)
    : [{ cat, items: filtered }];

  return (
    <section id="menu" data-testid="menu-section" className="py-28 lg:py-40 border-t border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p className="text-xs uppercase tracking-[0.35em] text-[#555] mb-6"
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          {t.menu.overline}
        </motion.p>
        <motion.h2 data-testid="menu-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-14"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.1 }}>
          {t.menu.title}
        </motion.h2>

        <div data-testid="menu-tabs" className="sticky top-16 z-30 bg-[#FAF9F6] flex flex-nowrap lg:flex-wrap overflow-x-auto lg:overflow-visible gap-x-8 gap-y-3 mb-16 border-b border-[#E5E5E5] py-5">
          {CATS.map((c) => (
            <button key={c} data-testid={`menu-tab-${c}`} onClick={() => setCat(c)}
              className={`text-xs uppercase tracking-[0.25em] whitespace-nowrap transition-colors duration-300 ${cat === c ? "text-[#6B1E33] font-bold" : "text-[#0A0A0A] hover:text-[#6B1E33]"}`}>
              {t.menu.cats[c]}
            </button>
          ))}
        </div>

        {loading ? (
          <p data-testid="menu-loading" className="text-sm text-[#555] py-10">{t.menu.loading}</p>
        ) : (
          <div className="grid lg:grid-cols-[1fr_360px] gap-16 items-start">
            <div data-testid="menu-list">
              {groups.map((group) => (
                <div key={group.cat} data-cat={group.cat} ref={(el) => (groupRefs.current[group.cat] = el)}>
                  {cat === "all" && (
                    <div data-testid={`menu-group-${group.cat}`} className="sticky top-[108px] z-20 bg-[#FAF9F6] py-4 border-b border-[#E5E5E5]">
                      <span className="font-serif italic text-2xl text-[#6B1E33]">{t.menu.cats[group.cat]}</span>
                    </div>
                  )}
                  {group.items.map((item, i) => (
                    <motion.div
                      key={item.id}
                      data-testid={`menu-item-${item.id}`}
                      className={`border-t border-[#E5E5E5] py-7 px-2 -mx-2 flex items-baseline justify-between gap-6 cursor-default transition-all duration-300 hover:px-5 hover:bg-white ${!item.available ? "opacity-40" : ""}`}
                      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: item.available ? 1 : 0.4, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6, delay: Math.min(i * 0.05, 0.4) }}
                    >
                      <div className="min-w-0">
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <h3 className="font-serif text-xl sm:text-2xl">{lang === "it" ? item.name_it : item.name_en}</h3>
                          {!item.available && <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B1E33]">{t.menu.soldout}</span>}
                        </div>
                        <p className="text-sm text-[#555] mt-1.5 leading-relaxed">{lang === "it" ? item.desc_it : item.desc_en}</p>
                      </div>
                      <span className="font-serif italic text-xl whitespace-nowrap">€ {item.price.toFixed(2).replace(".", ",")}</span>
                    </motion.div>
                  ))}
                </div>
              ))}
              <div className="border-t border-[#E5E5E5]" />
              <p className="text-xs text-[#777] mt-8 tracking-wide">{t.menu.note}</p>
            </div>

            <div className="hidden lg:block sticky top-28">
              <motion.div
                key={effectiveCat}
                data-testid="menu-preview-image"
                className="overflow-hidden border border-[#E5E5E5]"
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
              >
                <img src={imgUrl(previewImg)} alt={t.menu.cats[effectiveCat]} className="w-full aspect-[3/4] object-cover" />
              </motion.div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#555] mt-4 text-center">{t.menu.cats[effectiveCat]}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
